import { SiteHeader } from "@/components/site-header"
import { ScissorsIcon } from "lucide-react"

export default function SplitTripPage() {
  return (
    <>
      <SiteHeader title="Dividir viaje" />
      <div className="flex flex-1 flex-col px-4 py-6 lg:px-6">
        <div className="max-w-5xl space-y-12">

          {/* ── Descripción ── */}
          <section>
            <h2 className="text-base font-semibold mb-2">¿Qué es dividir un viaje?</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              La función <strong>Dividir viaje</strong> (ícono{" "}
              <ScissorsIcon className="inline size-3.5 align-text-bottom" />) permite partir un viaje en curso en
              dos tramos cuando ocurre un evento en ruta: rotura de camión, cambio de chofer, relevo de
              vehículo, etc. El viaje original conserva su origen y toma como nuevo destino la{" "}
              <strong>ciudad de corte</strong>. Un segundo viaje se crea desde esa ciudad hacia el destino
              original, con los recursos que se configuren en el formulario.
            </p>
          </section>

          {/* ── Flujo paso a paso ── */}
          <section>
            <h2 className="text-base font-semibold mb-4">Flujo paso a paso</h2>
            <ol className="space-y-4 text-sm">
              {[
                {
                  n: "1",
                  title: "Abrir el formulario",
                  desc: 'En el detalle de una carga, en la sección Viajes, hacer clic en el ícono de tijera (✂) de la fila del viaje a dividir. El formulario sólo aparece en modo edición y con permiso de escritura en Envíos.',
                },
                {
                  n: "2",
                  title: "Elegir la ciudad de corte",
                  desc: "La ciudad de corte será el nuevo destino del Tramo 1 y el origen del Tramo 2. El selector muestra todas las ciudades activas con búsqueda por nombre o código.",
                },
                {
                  n: "3",
                  title: "Seleccionar el móvil para el Tramo 2",
                  desc: "Se puede elegir el mismo móvil del viaje original (si sólo cambia el camión) o un móvil diferente. Los móviles inactivos aparecen deshabilitados. Al elegir uno, los campos Chofer, Camión y Semi se rellenan automáticamente con los recursos actuales del móvil.",
                },
                {
                  n: "4",
                  title: "Ajustar los recursos del Tramo 2",
                  desc: "Una vez seleccionado el móvil, aparece el panel de recursos. Se pueden cambiar de forma independiente el Chofer, el Camión y el Semi sin modificar el móvil en la base de datos. Estos valores se graban directamente en los campos driver_id, truck_id y trailer_id del nuevo viaje.",
                },
                {
                  n: "5",
                  title: "Confirmar",
                  desc: "Al confirmar: (1) el viaje original recibe PATCH con el nuevo destinationCityId, (2) se crea un nuevo viaje con POST /api/trips incluyendo mobileId, driverId, truckId, trailerId, originCityId = ciudad de corte y destinationCityId = destino original.",
                },
              ].map(({ n, title, desc }) => (
                <li key={n} className="flex gap-3">
                  <span className="size-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-semibold shrink-0 mt-0.5">{n}</span>
                  <div>
                    <p className="font-medium">{title}</p>
                    <p className="text-muted-foreground">{desc}</p>
                  </div>
                </li>
              ))}
            </ol>
          </section>

          {/* ── 4 casos ── */}
          <section>
            <h2 className="text-base font-semibold mb-2">Casos de uso cubiertos</h2>
            <p className="text-sm text-muted-foreground mb-4">
              El panel de recursos del Tramo 2 se puede editar libremente para cubrir cualquier
              combinación de relevo en ruta.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              {[
                {
                  caso: "Caso 1",
                  titulo: "Rotura de camión — mismo chofer y trailer",
                  desc: "Llega un camión nuevo. El chofer y el semi siguen iguales. Se selecciona el mismo móvil u otro, se cambia sólo el Camión en el panel.",
                  pasos: ["Mismo móvil (o nuevo)", "Camión → patente del camión nuevo", "Chofer → igual al original", "Semi → igual al original"],
                },
                {
                  caso: "Caso 2",
                  titulo: "Relevo completo de móvil",
                  desc: "Llega otro móvil completo (nuevo chofer, camión y trailer). Se selecciona el nuevo móvil y los recursos se auto-completan.",
                  pasos: ["Nuevo móvil", "Chofer, Camión, Semi → los del nuevo móvil (auto-completados)"],
                },
                {
                  caso: "Caso 3",
                  titulo: "Chofer se descompone — sube al nuevo camión con el mismo trailer",
                  desc: "Llega un nuevo móvil. El chofer original sube al nuevo camión y engancha su trailer. Se selecciona el nuevo móvil y se cambia el Semi por el del viaje original.",
                  pasos: ["Nuevo móvil", "Chofer → el del nuevo móvil (o el original)", "Semi → patente del trailer original"],
                },
                {
                  caso: "Caso 4",
                  titulo: "Rotura de camión — chofer original sigue con su trailer",
                  desc: "Llega un camión nuevo. El chofer y el trailer del viaje original enganchan al camión nuevo. Se selecciona el nuevo móvil y se sobreescriben chofer y semi con los originales.",
                  pasos: ["Nuevo móvil", "Camión → el del nuevo móvil", "Chofer → el del viaje original", "Semi → el del viaje original"],
                },
              ].map(({ caso, titulo, desc, pasos }) => (
                <div key={caso} className="rounded-lg border p-4 flex flex-col gap-2 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-semibold uppercase tracking-wide bg-primary/10 text-primary px-2 py-0.5 rounded-full">{caso}</span>
                    <p className="font-medium leading-tight">{titulo}</p>
                  </div>
                  <p className="text-muted-foreground text-xs leading-relaxed">{desc}</p>
                  <ul className="mt-1 space-y-0.5">
                    {pasos.map((p, i) => (
                      <li key={i} className="text-xs text-muted-foreground flex items-start gap-1.5">
                        <span className="text-primary/60 mt-0.5">→</span>
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>

          {/* ── API ── */}
          <section>
            <h2 className="text-base font-semibold mb-4">Endpoints involucrados</h2>
            <div className="rounded-lg border overflow-hidden text-sm">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground w-16">Método</th>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground w-60">Endpoint</th>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">Acción</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {[
                    ["PATCH", "/api/trips/:id", "Actualiza el viaje original: sólo destinationCityId = ciudad de corte."],
                    ["POST",  "/api/trips",     "Crea el Tramo 2 con mobileId, driverId, truckId, trailerId, originCityId (ciudad de corte) y destinationCityId (destino original)."],
                  ].map(([method, path, desc]) => (
                    <tr key={path}>
                      <td className="px-4 py-3">
                        <span className={`font-mono text-xs px-1.5 py-0.5 rounded font-semibold ${method === "POST" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                          {method}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs">{path}</td>
                      <td className="px-4 py-3 text-muted-foreground">{desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Los recursos del Tramo 2 (<code className="bg-muted px-1 py-0.5 rounded">driverId</code>,{" "}
              <code className="bg-muted px-1 py-0.5 rounded">truckId</code>,{" "}
              <code className="bg-muted px-1 py-0.5 rounded">trailerId</code>) se graban directamente
              en el trip y <strong>no modifican la tabla de móviles</strong>.
            </p>
          </section>

          {/* ── Restricciones ── */}
          <section>
            <h2 className="text-base font-semibold mb-3">Restricciones y consideraciones</h2>
            <ul className="space-y-2 text-sm text-muted-foreground">
              {[
                "La función está disponible sólo en modo edición y con permiso write en Envíos.",
                "Ciudad de corte y Móvil para el Tramo 2 son obligatorios para confirmar.",
                "El Tramo 2 hereda el shipmentId de la carga original.",
                "El estado del Tramo 2 se inicializa como Pendiente.",
                "No se puede dividir un viaje que ya fue eliminado (soft-deleted).",
                "Seleccionar el mismo móvil es válido: permite cambiar sólo el camión conservando chofer y trailer.",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-muted-foreground/40 mt-0.5 shrink-0">•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>

        </div>
      </div>
    </>
  )
}
